import { useRef, useEffect, useState, useCallback } from 'react'
import { useThree, useFrame, createPortal } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { CameraControls as CameraControlsComponent, BakeShadows } from '@react-three/drei'
import {
  DEFAULT_CAMERA_POSITIONS,
  CAMERA_CONTROLS_DEFAULTS,
  ANIMATION_TIMINGS,
  getDefaultCameraPosition,
  getPOICameraLimits
} from './camera/CameraConfig.js'
import { CameraMovementController } from './camera/CameraController.jsx'

/**
 * SceneManager - Manages scenes rendering to separate textures
 * 
 * This is a scalable architecture - currently configured for one scene
 * but designed to easily add more scenes in the future by:
 * 1. Adding scene content props (scene2Content, etc.)
 * 2. Adding render target refs
 * 3. Adding onSceneXTexture callbacks
 * 4. Adding conditional rendering logic
 */
export default function SceneManager({ 
  scene1Content,
  onScene1Texture,
  renderScene1 = true,
  mountScene1 = true,
  onScenesReady,
  cameraZoom = 2000,
  shouldBakeShadows = false,
  cameraRotation = 0,
  isAtPOI = false,
  activePOI = null
}) {
  const { gl, size, domElement, camera: canvasCamera, scene: canvasScene, set } = useThree()

  // The render-to-texture path cannot work on WebGPU. WebGPUBackend caches a RenderContext keyed
  // partly on renderTarget.samples and does not invalidate it on resize, so after any resize the
  // cached context references destroyed MSAA/depth textures:
  //   GPUValidationError: Destroyed texture [Texture "-msaa"] used in a submit
  // fired once per frame, forever. Four disposal-timing fixes were tried on 2026-07-21 and all
  // failed — the constraint is architectural, not a timing bug. On WebGPU we therefore render the
  // scene graph directly through R3F instead of via a WebGLRenderTarget.
  //
  // Consequence: the RTT colour grade (saturation 1.33, highlights/shadows 0.66, warmth 0.033)
  // lives on the ScreenPlane and does not exist on this path. The WebGL path is unchanged.
  const isWebGPU = !!gl?.isWebGPURenderer

  // Store the initial camera state once
  const initialCameraState = useRef(null)
  if (!initialCameraState.current) {
    initialCameraState.current = {
      position: canvasCamera.position.clone(),
      quaternion: new THREE.Quaternion().copy(canvasCamera.quaternion),
      zoom: canvasCamera.zoom,
      far: canvasCamera.far,
      near: canvasCamera.near
    }
  }

  // CRITICAL: Lock the orthographic canvas camera in place - it must NEVER move
  // Run BOTH early (before everything) AND late (after everything) to catch all modifications
  useFrame(() => {
    // On WebGPU there is no fullscreen ScreenPlane to hold steady — the canvas camera IS the
    // scene camera, so locking it would freeze the view and break every POI flight.
    if (isWebGPU) return
    canvasCamera.position.copy(initialCameraState.current.position)
    canvasCamera.quaternion.copy(initialCameraState.current.quaternion)
    canvasCamera.zoom = initialCameraState.current.zoom
    canvasCamera.updateProjectionMatrix()
  }, -10000) // Run BEFORE everything else
  
  // NOTE the priority: 0 on WebGPU, 10000 on WebGL.
  //
  // R3F counts every useFrame with priority > 0 (`internal.priority += priority > 0 ? 1 : 0`)
  // and each such subscriber is expected to render. Returning early inside the callback does
  // NOT undo that — the subscription is still counted. Leaving this at 10000 on WebGPU meant a
  // render pass ran after the composer's, so the scene was composited more than once per frame.
  useFrame(() => {
    if (isWebGPU) return
    canvasCamera.position.copy(initialCameraState.current.position)
    canvasCamera.quaternion.copy(initialCameraState.current.quaternion)
    canvasCamera.zoom = initialCameraState.current.zoom
    canvasCamera.updateProjectionMatrix()
  }, isWebGPU ? 0 : 10000)

  
  // Camera FOV values (hardcoded)
  const cameraControls = {
    scene1Fov: 24,
  }
  
  // Create scene
  const scene1Ref = useRef(new THREE.Scene())
  
  const camera1Ref = useRef(null)
  
  // Create render target for scene
  const renderTarget1Ref = useRef(null)
  
  // Ref for OrbitControls
  const controlsRef = useRef(null)
  
  // Track if initial target has been set
  const initialTargetSetRef = useRef(false)
  
  // Camera movement state (for CameraMovementController)
  const [moveTarget, setMoveTarget] = useState(null)
  const [moveAnimate, setMoveAnimate] = useState(false)
  
  // Camera limit state - tracks current rotation limits
  const [currentLimits, setCurrentLimits] = useState(CAMERA_CONTROLS_DEFAULTS.scene1)
  
  // Expose scenes and camera to parent via callback ref on CameraControlsComponent.
  // Fires at the exact moment the controls instance is available.
  const handleControlsRef = useCallback((instance) => {
    controlsRef.current = instance
    if (instance && onScenesReady && camera1Ref.current) {
      onScenesReady({
        // Hotspots traverse this scene for POI_* nodes to project label positions. On WebGPU the
        // content is portalled into R3F's own scene, so the private scene1Ref is empty — emitting
        // it would make getSceneHotspots find zero POIs and render no markers or labels.
        scene1: isWebGPU ? { current: canvasScene } : scene1Ref,
        camera: camera1Ref,
        controls: instance
      })
    }
  }, [onScenesReady, isWebGPU, canvasScene])

  // Expose controls to window for direct access
  useEffect(() => {
    if (controlsRef.current) {
      window.__cameraControls = controlsRef.current
    }
  }, [])

  // Apply POI-specific camera limits when viewing a POI
  useEffect(() => {
    if (isAtPOI && activePOI) {
      const poiLimits = getPOICameraLimits(activePOI)
      if (poiLimits) {
        setCurrentLimits(poiLimits)
      }
    } else {
      // Reset to default scene limits when not at a POI
      setCurrentLimits(CAMERA_CONTROLS_DEFAULTS.scene1)
    }
  }, [isAtPOI, activePOI])
  
  // Listen for camera movement events
  useEffect(() => {
    function handleSceneCameraMove(event) {
      if (event.detail) {
        const {
          targetPosition,
          targetLookAt,
          duration = ANIMATION_TIMINGS.cameraMovement
        } = event.detail

        if (targetPosition && targetLookAt) {
          setMoveTarget({
            position: targetPosition,
            targetLookAt,
            duration
          })
          setMoveAnimate(true)
        }
      }
    }

    function handleCameraSnap(event) {
      if (event.detail) {
        const { targetPosition, targetLookAt } = event.detail;
        setMoveTarget({
          position: targetPosition ? targetPosition.toArray() : null,
          targetLookAt: targetLookAt ? targetLookAt.toArray() : null
        });

        // First, unmount the old controller by setting moveAnimate to false
        setMoveAnimate(false);
        
        // Then immediately (in next render) mount a new controller with 1ms duration
        setTimeout(() => {
          setMoveTarget({
            position: targetPosition,
            targetLookAt,
            duration: 1
          });
          setMoveAnimate(true);
        }, 0);
      }
    }

    window.addEventListener('scene-camera-move', handleSceneCameraMove)
    window.addEventListener('scene-camera-snap', handleCameraSnap)
    return () => {
      window.removeEventListener('scene-camera-move', handleSceneCameraMove)
      window.removeEventListener('scene-camera-snap', handleCameraSnap)
    }
  }, [])
  
  useEffect(() => {
    // Create camera only once (not on every resize)
    if (!camera1Ref.current) {
      // Improved near/far values to reduce z-fighting
      // near: 0.5 instead of 0.1 (5x improvement in precision)
      // far: 1000 instead of 2000 (reduces far plane, improves precision)
      const cam1 = new THREE.PerspectiveCamera(cameraControls.scene1Fov, size.width / size.height, 0.5, 1000)

      // Set initial positions from shared defaults based on orientation
      const isPortrait = size.width < size.height
      const isLandscape = !isPortrait
      
      cam1.position.copy(getDefaultCameraPosition('scene1', isLandscape).position)

      camera1Ref.current = cam1

      // On WebGPU this perspective camera IS the render camera — R3F draws its own scene with
      // whatever camera is default, so promote it. onScenesReady still emits this same camera,
      // so POI hotspot projection is unaffected.
      //
      // `manual: true` stops R3F's resize handler from reasserting the <Canvas camera={}> prop
      // over this one; without it the orthographic prop camera wins and the scene renders from
      // the wrong camera (visible as: hotspots vanish, camPos stays at the prop's position).
      if (isWebGPU) {
        cam1.aspect = size.width / size.height
        cam1.updateProjectionMatrix()
        set({ camera: cam1 })
      }
    } else {
      // On resize: only update aspect ratio, don't recreate camera
      camera1Ref.current.aspect = size.width / size.height
      camera1Ref.current.updateProjectionMatrix()
    }

    // WebGPU renders the graph directly — no render target, so nothing to size or dispose.
    if (isWebGPU) return

    // Get device pixel ratio for high DPI displays (clamp to reduce performance impact)
    const pixelRatio = Math.min(window.devicePixelRatio, 1.5)
    // WebGPURenderer has no .capabilities object; it exposes getMaxAnisotropy() directly.
    const maxAnisotropy = gl.capabilities?.getMaxAnisotropy?.() ?? gl.getMaxAnisotropy?.() ?? 1

    // Create optimized render target with reduced quality for performance
    const rt1 = new THREE.WebGLRenderTarget(
      size.width * pixelRatio,
      size.height * pixelRatio,
      {
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType,
        colorSpace: THREE.LinearSRGBColorSpace,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        stencilBuffer: false,
        samples: 4,
        anisotropy: Math.min(maxAnisotropy, 4),
      }
    )

    renderTarget1Ref.current = rt1

    // Provide texture to parent immediately (even if not rendering yet)
    if (onScene1Texture) onScene1Texture(rt1.texture)

    return () => {
      rt1.dispose()
    }
  }, [size.width, size.height, cameraControls.scene1Fov])

  // Set initial camera position AND target via camera-controls API - only once when controls first exist
  // Must use setLookAt() so the controls' internal state matches the camera object.
  // Calling only setTarget() leaves the internal position at (0,0,0) which causes
  // the first animated move to lerp from the wrong start point.
  useFrame(() => {
    if (controlsRef.current && !initialTargetSetRef.current) {
      const isPortrait = window.innerWidth < window.innerHeight
      const isLandscape = !isPortrait
      const defaults = getDefaultCameraPosition('scene1', isLandscape)
      const p = defaults.position
      const t = defaults.target
      controlsRef.current.setLookAt(p.x, p.y, p.z, t.x, t.y, t.z, false)
      initialTargetSetRef.current = true
    }
  })
  
  // Saxavord scene - no POI-specific camera limits needed
  // Camera controls remain at default limits for all POI views
  
  // Update camera FOV based on device zoom level (from app.jsx)
  // For PerspectiveCamera: lower FOV = more zoomed in
  // cameraZoom: 400 (portrait) to 2000 (landscape)
  // Convert zoom to FOV: FOV = 75 / (zoom / 2000)
  useEffect(() => {
    if (camera1Ref.current) {
      // Calculate FOV based on zoom value
      // Base FOV at zoom 2000 is 24 degrees
      // As zoom decreases (portrait mode), increase FOV to show less (zoom in effect)
      const fovMultiplier = 2200 / cameraZoom
      const newFov = 24 * fovMultiplier
      
      camera1Ref.current.fov = newFov
      // Zoom is an orthographic concept; on WebGPU this camera is the live render camera and
      // any leftover zoom !== 1 would scale the view. FOV alone drives framing here.
      if (isWebGPU) camera1Ref.current.zoom = 1
      camera1Ref.current.updateProjectionMatrix()
    }
  }, [cameraZoom, isWebGPU])

  // Render scene to texture
  // Priority 1 (runs after priority 0) ensures camera-controls has already
  // called its own update() before we render the scene to the texture.
  useFrame(() => {
    if (!camera1Ref.current) return

    // Apply camera roll (dutch angle) if cameraRotation is provided
    if (cameraRotation !== 0) {
      // Create a quaternion for the roll around Z-axis (in camera's local space)
      const rollQuat = new THREE.Quaternion()
      rollQuat.setFromAxisAngle(new THREE.Vector3(0, 0, 1), cameraRotation)
      // Apply the roll after all other rotations
      camera1Ref.current.quaternion.multiplyQuaternions(camera1Ref.current.quaternion, rollQuat)
    }
    
    // Update matrices
    camera1Ref.current.updateMatrixWorld()
    
    // A useFrame with a priority argument puts R3F into MANUAL render mode — it stops rendering
    // automatically and expects this callback to do it. On the RTT path that is exactly what the
    // block below does (render into the target; ScreenPlane then draws the texture).
    //
    // On WebGPU there is no ScreenPlane, so this callback must render the scene to the screen
    // itself. Returning early here means nothing renders at all — not even an unlit cube.
    if (isWebGPU) {
      // PostProcessingWebGPU publishes its composer here when enabled. When present it owns the
      // frame — renderAsync() runs the scene pass plus bloom and tone mapping — otherwise render
      // the scene straight to the screen.
      const post = gl.userData?.postProcessing
      if (post) {
        // render() not renderAsync() — the latter is deprecated in r185, and the renderer is
        // already initialised by the async gl factory in app.jsx.
        post.render()
      } else {
        gl.render(canvasScene, camera1Ref.current)
      }
      return
    }

    // Render scene to texture (conditionally)
    if (renderTarget1Ref.current) {
      const originalRenderTarget = gl.getRenderTarget()
      
      // Render scene 1 only if needed
      if (renderScene1) {
        gl.setRenderTarget(renderTarget1Ref.current)
        gl.clear()
        gl.render(scene1Ref.current, camera1Ref.current)
        renderTarget1Ref.current.texture.needsUpdate = true
      }
      
      // Restore original render target
      gl.setRenderTarget(originalRenderTarget)
    }
  }, 1)
  
  return (
    <>
      {/* Camera movement controller - handles smooth camera animations */}
      {moveAnimate && moveTarget && camera1Ref.current && controlsRef.current && (
        <CameraMovementController
          camera={camera1Ref.current}
          controls={controlsRef.current}
          targetPosition={moveTarget.position}
          targetLookAt={moveTarget.targetLookAt}
          animate={moveAnimate}
          duration={moveTarget.duration}
          onAnimationComplete={() => setMoveAnimate(false)}
        />
      )}
      
      {/* CameraControls for camera movement */}
      {camera1Ref.current && (
        <CameraControlsComponent
          ref={handleControlsRef}
          camera={camera1Ref.current}
          domElement={domElement}
          minDistance={CAMERA_CONTROLS_DEFAULTS.minDistance}
          maxDistance={CAMERA_CONTROLS_DEFAULTS.maxDistance}
          enableZoom={CAMERA_CONTROLS_DEFAULTS.enableZoom}
          maxPolarAngle={currentLimits.maxPolarAngle}
          minPolarAngle={currentLimits.minPolarAngle}
          maxAzimuthAngle={currentLimits.maxAzimuthAngle}
          minAzimuthAngle={currentLimits.minAzimuthAngle}
          enabled={!moveAnimate}
          smoothTime={CAMERA_CONTROLS_DEFAULTS.smoothTime}
          enablePan={CAMERA_CONTROLS_DEFAULTS.enablePan}
          mouseButtons={{
            left: 1,
            middle: 0,
            right: 0,
            wheel: 0,
          }}
          touches={{
            one: 1,
            two: 0,
            three: 0,
          }}
        />
      )}
      
      {/* Portal Scene 1: Launch Platform - conditionally mount to reduce draw calls.
          On WebGPU the portal target is R3F's own scene, so the graph is rendered directly by
          R3F rather than blitted through a render target. The private scene1Ref scene is still
          handed to onScenesReady on both paths, keeping that contract stable. */}
      {mountScene1 && createPortal(
        <>
          {/* Black clear colour only on the RTT path, where this private scene is rendered into
              a texture. On WebGPU the portal target IS R3F's scene, so painting it black here
              would overwrite the decoded sky texture that <Environment background> just set. */}
          {!isWebGPU && <color attach="background" args={['#000000']} />}
          {scene1Content}
          {shouldBakeShadows && <BakeShadows />}
        </>,
        isWebGPU ? canvasScene : scene1Ref.current
      )}
    </>
  )
}
